import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { 
  Building2, 
  Users, 
  CreditCard, 
  Smartphone, 
  Shield, 
  TrendingUp, 
  MapPin, 
  CheckCircle, 
  FileText, 
  ArrowRight,
  Star,
  Banknote,
  Wifi,
  Award,
  Clock,
  Phone
} from 'lucide-react';

export default function CSPBankingPage() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeService, setActiveService] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const services = [
    {
      category: "Banking Services",
      icon: <Building2 className="w-6 h-6" />,
      items: [
        "Saving / Jan Dhan Account Opening",
        "Cash Deposit / Withdrawal via AEPS or Micro ATM",
        "Balance Enquiry & Mini Statement",
        "Fund Transfers to Any Bank"
      ]
    },
    {
      category: "Digital & Utility Services",
      icon: <Smartphone className="w-6 h-6" />,
      items: [
        "Recharge & Bill Payments (Electricity, Gas, DTH, Mobile)",
        "PAN Card Services",
        "Insurance Enrollment – PMJJBY, PMSBY, Atal Pension Yojana",
        "Loan Assistance & Enquiry",
        "Aadhar Seeding, eKYC, DBT Assistance"
      ]
    },
    {
      category: "Other Add-ons",
      icon: <CreditCard className="w-6 h-6" />,
      items: [
        "Mini ATM Services",
        "Voter ID, Passport & Other Government Service Support",
        "Train/Bus/Flight Ticket Booking (Optional)"
      ]
    }
  ];

  const benefits = [
    { icon: <TrendingUp />, title: "Monthly Income", desc: "Commission-Based Earnings" },
    { icon: <Shield />, title: "Authorized License", desc: "From Nationalized Banks or Private Bank" },
    { icon: <Award />, title: "Zero Qualification", desc: "No Need for Banking Qualification" },
    { icon: <Users />, title: "Complete Support", desc: "Training & Technical Support" },
    { icon: <Star />, title: "Community Impact", desc: "Uplift Your Area & Earn Respect" }
  ];

  const steps = [
    { step: 1, title: "Fill Application", desc: "Online or Offline Form" },
    { step: 2, title: "Submit Documents", desc: "Upload KYC Documents" },
    { step: 3, title: "Verification", desc: "Documents & Location Visit" },
    { step: 4, title: "Training", desc: "Orientation & Online Training" },
    { step: 5, title: "Start Earning", desc: "Get CSP Code & Begin" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br pt-12 from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <header className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative container mx-auto px-4 py-16 md:py-24 text-center text-white">
          <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <Building2 className="w-5 h-5" />
              <span className="text-sm font-medium">Banking Partner Program</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Start Your Own <span className="text-yellow-300">CSP Point</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 max-w-3xl mx-auto">
              Transform Your Shop into a Digital Banking Kendra
            </p>
            <p className="text-lg mb-10 opacity-80 max-w-2xl mx-auto">
              Join hands with leading Indian banks and become a Customer Service Point (CSP) Operator
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-yellow-400 hover:bg-yellow-300 text-black px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105 flex items-center gap-2">
                Apply Now <ArrowRight className="w-5 h-5" />
              </button>
          <Link to="/careers">
  <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-full font-semibold transition-all">
    Learn More
  </button>
</Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-blue-50 to-transparent"></div>
      </header>

      {/* Why CSP Section */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Why CSP is the <span className="text-blue-600">Future of Rural Banking</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              India is going digital. But millions in rural and semi-urban areas still lack direct access to basic banking services. 
              That's where CSP Points play a key role — providing a bridge between banks and the common man.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Local Presence</h3>
              <p className="text-gray-600">Serve your community directly, whether in village, town, or city locations.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6">
                <Wifi className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Digital Bridge</h3>
              <p className="text-gray-600">Connect traditional banking with modern digital solutions and biometric devices.</p>
            </div>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-4">Banking Mitra</h3>
              <p className="text-gray-600">Act as a trusted banking partner helping with withdrawals, deposits, and more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Services Available Through <span className="text-blue-600">CSP Point</span>
            </h2>
            <p className="text-lg text-gray-600">Your CSP outlet can offer a wide range of essential services</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg cursor-pointer transition-all transform hover:scale-105 ${
                  activeService === index ? 'ring-4 ring-blue-200 shadow-xl' : ''
                }`}
                onClick={() => setActiveService(index)}
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">{service.category}</h3>
                </div>
                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Should Apply */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-3xl p-8 md:p-16 text-white">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">Who Should Apply?</h2>
              <p className="text-xl opacity-90">Perfect for entrepreneurs and community-minded individuals</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: <Building2 />, text: "Shop owner, kiosk operator, or CSC agent" },
                { icon: <Clock />, text: "Seeking part-time/full-time income" },
                { icon: <TrendingUp />, text: "Looking to start low-investment business" },
                { icon: <Users />, text: "Passionate to serve your community" }
              ].map((item, index) => (
                <div key={index} className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    {item.icon}
                  </div>
                  <p className="text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Benefits of Becoming a <span className="text-green-600">CSP Operator</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-6 text-green-600">
                  {benefit.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.desc}</p>
                <div className="mt-4">
                  <CheckCircle className="w-6 h-6 text-green-500" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Required */}
      <section className="py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                <FileText className="w-10 h-10 inline-block mr-4 text-blue-600" />
                Documents Required
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
              {[
                "Aadhar Card, PAN Card",
                "Passport-sized Photographs",
                "Shop Address Proof",
                "Bank Account Details",
                "Police Verification (as per bank norms)"
              ].map((doc, index) => (
                <div key={index} className="bg-blue-50 rounded-2xl p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 text-blue-600" />
                  </div>
                  <p className="text-gray-700 font-medium">{doc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="container mx-auto px-4 text-white">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">How to Apply for CSP Point?</h2>
            <p className="text-xl opacity-90">Simple 5-step process to get started</p>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {step.step}
                </div>
                <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                <p className="text-sm opacity-80">{step.desc}</p>
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-full w-full">
                    <ArrowRight className="w-6 h-6 text-white/60 mx-auto" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-yellow-400">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Apply Now – Limited Slots Per Area!
          </h2>
          <p className="text-lg text-black/80 mb-8 max-w-2xl mx-auto">
            Don't miss this opportunity to become a banking partner in your community. 
            Start your journey towards financial independence today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://forms.gle/tWonb67p5kAiNxsF6"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-yellow-400 px-10 py-4 rounded-full font-bold text-lg transition-all transform hover:scale-105 hover:shadow-lg inline-flex items-center gap-3"
            >
              <Building2 className="w-6 h-6" />
              Apply for CSP Point
              <ArrowRight className="w-6 h-6" />
            </a>
              <Link to="/careers">
            <button className="border-2 border-black text-black hover:bg-black hover:text-yellow-400 px-8 py-4 rounded-full font-semibold transition-all inline-flex items-center gap-2">
              Contact Us
            </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="mb-8">
            <Building2 className="w-12 h-12 mx-auto mb-4 text-blue-400" />
            <h3 className="text-2xl font-bold mb-2">CSP Banking Partner</h3>
            <p className="text-gray-400">Empowering communities through digital banking solutions</p>
          </div>
          <div className="border-t border-gray-800 pt-8">
            <p className="text-gray-400">© 2025 CSP Banking Partner Program. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}